/**
 * BulkActionsBar Component - Full-Width Bottom Bar (Spark Design)
 *
 * Modern floating action bar for bulk geometry operations.
 * Positioned absolutely at panel bottom with full width.
 * Compact Spark-style layout: [Icon + Count + Clear] [Spacer] [Actions]
 * Includes smooth slide-up/down animations.
 *
 * @module components/map/GeometriesList/core
 */

import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Group, Text, Button, ActionIcon, Tooltip } from '@mantine/core';
import { IconTrash, IconX, IconCheckbox } from '@tabler/icons-react';
import type { BulkActionsBarProps } from '../types';
import {
  ICON_SIZE_SM,
  ICON_SIZE_MD,
  ICON_SIZE_LG,
  BULK_ACTIONS_ANIMATION_DURATION,
} from '../../../../constants/ui';
import { Z_INDEX_TOOLTIP } from '../../../../constants/zIndex';
import classes from '../styles/BulkActionsBar.module.css';

/**
 * BulkActionsBar Component
 *
 * Floating toolbar that appears at bottom of parent panel when geometries are selected.
 * Provides bulk delete and clear selection actions.
 *
 * Layout (Spark design):
 * - Full panel width (edge-to-edge)
 * - Positioned at very bottom (bottom: 0)
 * - Rounded corners (lg)
 * - Compact: [Clear + Icon + Count] [Spacer] [Delete]
 *
 * @example
 * <BulkActionsBar
 *   selectedCount={2}
 *   onDeleteSelected={() => handleDelete()}
 *   onClearSelection={() => handleClear()}
 * />
 */
export const BulkActionsBar = memo(function BulkActionsBar({
  selectedCount,
  onDeleteSelected,
  onClearSelection,
  className,
}: BulkActionsBarProps) {
  const { t } = useTranslation();
  const [animationState, setAnimationState] = useState<'entering' | 'exiting' | null>(null);

  // Handle visibility and animation states
  useEffect(() => {
    if (selectedCount > 0) {
      // Show: trigger entrance animation
      setAnimationState('entering');
    } else if (selectedCount === 0 && animationState !== null) {
      // Hide: trigger exit animation, then unmount after animation completes
      setAnimationState('exiting');
      const timer = setTimeout(() => setAnimationState(null), BULK_ACTIONS_ANIMATION_DURATION);
      return () => clearTimeout(timer);
    }
    return undefined; // Explicit return for TypeScript
  }, [selectedCount, animationState]);

  if (animationState === null) {
    return null;
  }

  return (
    <div
      className={`${classes.container} ${classes[animationState]} ${className || ''}`}
    >
      <div className={classes.content}>
        {/* Left group: Clear + Icon + Count (Spark design) */}
        <Group gap="xs" className={classes.leftGroup}>
          <Tooltip
            label={t('geometries.bulkActions.clearSelection')}
            zIndex={Z_INDEX_TOOLTIP}
            events={{ hover: true, focus: true, touch: false }}
          >
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={onClearSelection}
              className={classes.iconButton}
            >
              <IconX size={ICON_SIZE_MD} />
            </ActionIcon>
          </Tooltip>
          <IconCheckbox size={ICON_SIZE_LG} className={classes.checkboxIcon} />
          <Text className={classes.selectedText}>
            {t('geometries.bulkActions.selected', { count: selectedCount })}
          </Text>
        </Group>

        {/* Right group: Delete button only */}
        <Group gap="xs" className={classes.rightGroup}>
          <Button
            variant="filled"
            color="red"
            size="xs"
            leftSection={<IconTrash size={ICON_SIZE_SM} />}
            onClick={onDeleteSelected}
            className={classes.deleteButton}
          >
            {t('geometries.bulkActions.deleteSelected')}
          </Button>
        </Group>
      </div>
    </div>
  );
});
